import { useQuery, useQueryClient } from 'react-query';
import { useFirebase } from 'components/Firebase/index.js';
import { useOrgId } from 'hooks/index.js';
import { MEDIA_ASSET_STATE } from '../constants.js';
import { getProductDetection, isDetectingProducts } from '../aiProductSuggestions.js';
import { vodUrl } from '../../../../../client-config.js';

const PROCESSING_POLL_INTERVAL_MS = 3000;
const DETECTING_PRODUCTS_POLL_INTERVAL_MS = 15 * 1000;

// A video whose media is still being transcoded reaches `ready` through a
// Bambuser webhook the dashboard never hears about, so anything rendering the
// media has to ask again. `error` is terminal and deliberately not polled.
const isMediaProcessing = (video) => {
  const state = video?.data?.mediaAssets?.[0]?.state;
  return state === MEDIA_ASSET_STATE.INITIAL || state === MEDIA_ASSET_STATE.PROCESSING;
};

/**
 * How often to fetch the video again, or false. An AI product tagging run
 * reaches the page through the videos stream, but a stream that dropped misses
 * it for good, while a refetch of the list still picks it up. A caller showing
 * the run asks again while it is under way, so it cannot fall behind the list.
 */
export const getVideoRefetchInterval = (video, { pollWhileDetectingProducts = false } = {}) => {
  if (isMediaProcessing(video)) return PROCESSING_POLL_INTERVAL_MS;
  if (pollWhileDetectingProducts && isDetectingProducts(getProductDetection(video?.data))) {
    return DETECTING_PRODUCTS_POLL_INTERVAL_MS;
  }
  return false;
};

/**
 * The video from the list queries in the cache, as `{ video, updatedAt }`, or
 * null. Several lists can hold the same video — the library's page, and the
 * plain list other parts of the page load — and the videos stream only keeps
 * the library's up to date, so this takes the copy fetched or updated last,
 * with the time it was. It only takes a video whose media is ready.
 */
export const findVideoInListCache = (queryClient, orgId, videoId) => {
  const lists = queryClient.getQueryCache().findAll(['videos', orgId, {}])
    .filter((query) => Array.isArray(query.state.data?.data))
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  for (const query of lists) {
    const video = query.state.data.data.find((v) => v.id === videoId && v.mediaAssets?.[0]?.state === 'ready');
    if (video) return { video, updatedAt: query.state.dataUpdatedAt };
  }
  return null;
};

const useVideoQuery = ({ videoId }, { pollWhileDetectingProducts = false, ...options } = {}) => {
  const firebase = useFirebase();
  const orgId = useOrgId();
  const queryClient = useQueryClient();

  return useQuery({
    enabled: !!videoId && videoId !== 'edit-new',
    queryKey: ['videos', orgId, videoId],
    queryFn: async () => {
      const idToken = await firebase.getIdToken();
      const fetchUrl = new URL(`${vodUrl}/v1/orgs/${orgId}/videos/${videoId}`);

      const response = await fetch(fetchUrl.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      const { ok, status } = response;

      if (status === 404) return { data: null };
      if (!ok) throw new Error({ status });

      return await response.json();
    },
    initialData: () => {
      // Check if we already have the data in the cache from the videos query.
      // Returning undefined tells react-query to fetch the data instead
      const cached = findVideoInListCache(queryClient, orgId, videoId);
      return cached ? { data: cached.video } : undefined;
    },
    initialDataUpdatedAt: () => findVideoInListCache(queryClient, orgId, videoId)?.updatedAt,
    refetchInterval: (video) => getVideoRefetchInterval(video, { pollWhileDetectingProducts }),
    ...options,
  });
};

export default useVideoQuery;
